import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CheckboxGroup } from '@eb/eds-checkbox-group';

import './FilterCheckboxGroup.scss';

const FilterCheckboxGroup = (props) => {
    const elementId = `filter-checkbox-group__${props.name}--${
        props.isMobile ? 'mobile' : 'desktop'
    }`;

    useEffect(() => {
        /*
        check for checked checkboxes on value
        change for selected filters
        */
        setActiveClasses();
    }, [props.selectedFilters]);

    /*
    Queries dom for active checkboxes and
    assigns "active" classes to the
    checkbox wrapping components.

    Due to no way via CSS to select
    a parent, we need to leverage this approach
    in order to properly update the styling of
    the checkbox's parent element.
    */
    const setActiveClasses = () => {
        const applyClasses = (checkboxes, isActive) =>
            checkboxes.forEach((checkbox) => {
                const action = isActive ? 'add' : 'remove';
                const parent = checkbox.parentNode;
                const parentWrapper = parent.parentElement;

                parentWrapper.classList[action]('filter-checkbox--active');
            });

        const checkedCheckboxes = [
            ...document.querySelectorAll(
                `#${elementId} input[type='checkbox']:checked`,
            ),
        ];

        const unCheckedCheckboxes = [
            ...document.querySelectorAll(
                `#${elementId} input[type='checkbox']:not(:checked)`,
            ),
        ];

        applyClasses(checkedCheckboxes, true);
        applyClasses(unCheckedCheckboxes, false);
    };

    const handleCheckboxChange = (selectedOptions, { isChecked, value }) => {
        let parsedSelectedOptions = selectedOptions;

        /*
        We want to ensure that if the user selected an option with value
        of "all" (eg. Any language) we automatically uncheck any other
        checkboxes. Additionally, if the user selects a value when "all"
        is selected, we want to automatically uncheck that option.
        */
        if (value === 'all' && isChecked && parsedSelectedOptions.length > 1) {
            parsedSelectedOptions = 'all';
        } else if (value !== 'all' && isChecked) {
            parsedSelectedOptions = selectedOptions.filter(
                (option) => option !== 'all',
            );
        }

        return props.onClick(parsedSelectedOptions);
    };

    return (
        <div
            id={elementId}
            data-integration={`filter-checkbox-group--${props.name}`}
            className="filter-checkbox-group"
        >
            <CheckboxGroup
                values={props.values}
                onChange={handleCheckboxChange}
                value={
                    typeof props.selectedFilters === 'string'
                        ? [props.selectedFilters]
                        : props.selectedFilters
                }
                name={props.name}
            />
        </div>
    );
};

FilterCheckboxGroup.propTypes = {
    /*
     * Name of the checkbox group. Required
     */
    name: PropTypes.string.isRequired,
    /*
     * List of all possible values this checkbox
     * group should render.
     */
    values: PropTypes.arrayOf(
        PropTypes.shape({
            //Support object due to gettext
            display: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
            value: PropTypes.string,
        }),
    ).isRequired,
    /*
     * The values of the checkboxes which are currently selected. In some cases
     * will be passed a string representation which it handles by converting
     * into an array.
     */
    selectedFilters: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    /*
     * Boolean dictating if mobile screen or not
     */
    isMobile: PropTypes.bool,
    /*
     * Callback for when a checkbox is clicked
     */
    onClick: PropTypes.func,
};

export default FilterCheckboxGroup;
